import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CountUp from "react-countup";
import { formatCurrency } from "../lib/formatCurrency";
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Search, Package, MoreVertical, Tag, Clock, DollarSign, Percent } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'training', label: 'Training & Education' },
  { value: 'consulting', label: 'Consulting Services' },
  { value: 'software', label: 'Software & Licenses' },
  { value: 'support', label: 'Support & Maintenance' },
  { value: 'project', label: 'Project Services' },
  { value: 'general', label: 'General Services' },
  { value: 'other', label: 'Other' }
];

const UNITS = [
  { value: 'hour', label: 'Per Hour' },
  { value: 'day', label: 'Per Day' },
  { value: 'project', label: 'Per Project' },
  { value: 'license', label: 'Per License' },
  { value: 'subscription', label: 'Subscription (Monthly)' },
  { value: 'unit', label: 'Per Unit' }
];

const categoryColors = {
  training: 'bg-blue-100 text-blue-700',
  consulting: 'bg-purple-100 text-purple-700',
  software: 'bg-emerald-100 text-emerald-700',
  support: 'bg-amber-100 text-amber-700',
  project: 'bg-rose-100 text-rose-700',
  general: 'bg-slate-100 text-slate-700',
  other: 'bg-slate-100 text-slate-700'
};


// Service Form Component
const ServiceForm = ({ service, onSave, onClose }) => {
  const [form, setForm] = useState(service || {
    name: '',
    code: '',
    category: 'general',
    description: '',
    unit: 'hour',
    unit_price: 0,
    currency: 'ZAR',
    vat_inclusive: false,
    is_active: true,
    min_quantity: 1,
    max_discount_percent: 0,
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    if (tagInput && !form.tags.includes(tagInput)) {
      setForm({ ...form, tags: [...form.tags, tagInput] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (service?.id) {
        await axios.put(`${API}/services/${service.id}`, form);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/services`, form);
        toast.success('Service created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2 col-span-2">
          <Label>Service Name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g., Project Management Training" data-testid="service-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Internal Code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g., PM-TRAIN-01" />
        </div>
        <div className="space-y-2">
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Describe the service offering..." />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Unit Price (ZAR) *</Label>
          <Input type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: parseFloat(e.target.value) || 0 })} required />
        </div>
        <div className="space-y-2">
          <Label>Billing Unit</Label>
          <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>VAT Inclusive</Label>
          <div className="flex items-center h-10">
            <Switch checked={form.vat_inclusive} onCheckedChange={(v) => setForm({ ...form, vat_inclusive: v })} />
            <span className="ml-2 text-sm text-muted-foreground">{form.vat_inclusive ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Min Quantity</Label>
          <Input type="number" min="1" value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: parseInt(e.target.value) || 1 })} />
        </div>
        <div className="space-y-2">
          <Label>Max Discount (%)</Label>
          <Input type="number" min="0" max="100" value={form.max_discount_percent} onChange={(e) => setForm({ ...form, max_discount_percent: parseFloat(e.target.value) || 0 })} />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <div className="flex items-center h-10">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            <span className="ml-2 text-sm text-muted-foreground">{form.is_active ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tags</Label>
        <div className="flex gap-2">
          <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
          <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
        </div>
        {form.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {form.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                {tag} ×
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-service-btn">{saving ? 'Saving...' : 'Save Service'}</Button>
      </div>
    </form>
  );
};

// Main Services Page
export const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success('Service deleted');
      loadServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const filteredServices = services.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.service_code.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = filterCategory === 'all' || s.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Summary stats
  const totalServices = services.length;
  const activeServices = services.filter(s => s.is_active).length;
  const avgPrice = services.length > 0 ? services.reduce((sum, s) => sum + s.unit_price, 0) / services.length : 0;

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="services-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Services</h1>
          <p className="text-muted-foreground mt-1">Manage your service offerings and pricing</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setSelectedService(null)} data-testid="add-service-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedService ? 'Edit Service' : 'New Service'}</DialogTitle>
            </DialogHeader>
            <ServiceForm service={selectedService} onSave={loadServices} onClose={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Services</p>
                <p className="text-2xl font-bold font-mono"><p className="text-2xl font-bold font-mono">
  <CountUp end={totalServices} duration={1} />
</p></p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Tag className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Services</p>
                <p className="text-2xl font-bold font-mono text-emerald-600"><p className="text-2xl font-bold font-mono text-emerald-600">
  <CountUp end={activeServices} duration={1} />
</p></p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg. Price</p>
                <p className="text-2xl font-bold font-mono"><p className="text-2xl font-bold font-mono">
  <CountUp
    end={avgPrice}
    duration={1.5}
    decimals={2}
    formattingFn={formatCurrency}
  />
</p></p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search services..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Services Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Service Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredServices.map(service => (
              <TableRow key={service.id}>
                <TableCell className="font-mono text-sm">{service.service_code}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{service.name}</p>
                    {service.description && <p className="text-xs text-muted-foreground truncate max-w-[300px]">{service.description}</p>}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={categoryColors[service.category]}>
                    {CATEGORIES.find(c => c.value === service.category)?.label || service.category}
                  </Badge>
                </TableCell>
                <TableCell className="capitalize">{UNITS.find(u => u.value === service.unit)?.label || service.unit}</TableCell>
                <TableCell className="text-right">
                  <span className="font-mono font-semibold">{formatCurrency(service.unit_price)}</span>
                  {service.vat_inclusive && <span className="text-xs text-muted-foreground ml-1">(incl. VAT)</span>}
                </TableCell>
                <TableCell>
                  <Badge className={service.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                    {service.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setSelectedService(service); setDialogOpen(true); }}>Edit</DropdownMenuItem>
                      <DropdownMenuItem className="text-rose-600" onClick={() => handleDelete(service.id)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredServices.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No services found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
